import { useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MessagesSquare, Plus, MessageCircle, Search } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

export default function ForumPage() {
  const { user } = useAuth();
  const { openAuth } = useOutletContext();
  const qc = useQueryClient();
  const [cat, setCat] = useState(null);
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", category_id: "" });

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/forum/categories")).data });
  const { data: threads = [] } = useQuery({ queryKey: ["threads", cat], queryFn: async () => (await api.get(`/forum/threads${cat ? `?category_id=${cat}` : ""}`)).data });

  const filtered = threads.filter((t) => t.title.toLowerCase().includes(q.toLowerCase()));

  const create = useMutation({
    mutationFn: async () => (await api.post("/forum/threads", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["threads"] }); toast.success("Thread posted"); setDialogOpen(false); setForm({ title: "", body: "", category_id: "" }); },
    onError: (e) => toast.error(formatError(e.response?.data?.detail)),
  });

  const startThread = () => {
    if (!user) { openAuth(); return; }
    setForm((f) => ({ ...f, category_id: categories[0]?.id || "" }));
    setDialogOpen(true);
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "General";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <span className="label-tech text-primary">Community</span>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tighter font-black mt-2">Forum & Discussion</h1>
          <p className="text-muted-foreground mt-2">Field knowledge, code questions, and industry talk.</p>
        </div>
        <Button onClick={startThread} className="rounded-none bg-primary hover:bg-[#D62828] font-bold" data-testid="new-thread-btn"><Plus className="w-4 h-4 mr-1" /> New Thread</Button>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3">
          <span className="label-tech text-muted-foreground">Categories</span>
          <div className="mt-3 border border-border bg-white">
            <button onClick={() => setCat(null)} className={`w-full text-left px-4 py-3 border-b border-border text-sm font-semibold ${!cat ? "bg-primary text-white" : "hover:bg-secondary"}`} data-testid="cat-all">All Topics</button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} className={`w-full text-left px-4 py-3 border-b border-border last:border-0 text-sm font-semibold ${cat === c.id ? "bg-primary text-white" : "hover:bg-secondary"}`} data-testid={`cat-${c.slug}`}>
                {c.name}
              </button>
            ))}
          </div>
        </aside>

        <div className="lg:col-span-9">
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search threads…" className="rounded-none pl-10 h-12" data-testid="forum-search" />
          </div>
          <div className="border border-border bg-white divide-y divide-border">
            {filtered.map((t, i) => (
              <Link key={t.id} to={`/forum/${t.id}`} className="flex items-center justify-between p-5 hover:bg-secondary transition-colors animate-slide-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`thread-${t.id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-secondary border border-border flex items-center justify-center shrink-0">
                    <MessagesSquare className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">{t.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{catName(t.category_id)} · by {t.author_name}</p>
                  </div>
                </div>
                <span className="flex items-center gap-1 text-sm text-muted-foreground shrink-0"><MessageCircle className="w-4 h-4" /> {t.reply_count}</span>
              </Link>
            ))}
            {filtered.length === 0 && <p className="p-8 text-center text-muted-foreground text-sm">No threads yet. Be the first to post.</p>}
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none border-2 border-foreground max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Start a New Thread</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-tech">Category</Label>
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full h-10 border border-input px-3 mt-1 rounded-none bg-white" data-testid="thread-category-select">
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="label-tech">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none mt-1" data-testid="thread-title-input" />
            </div>
            <div>
              <Label className="label-tech">Message</Label>
              <Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={6} className="rounded-none mt-1" data-testid="thread-body-input" />
            </div>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !form.title} className="rounded-none bg-primary hover:bg-[#D62828] font-bold w-full" data-testid="thread-post-btn">
              {create.isPending ? "Posting…" : "Post Thread"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
